import React from 'react';
import Map, { Source, Layer as MapLayer, Marker, type MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

interface USMapWithRadiusProps {
  center: { lat: number; lng: number; label: string };
  radiusMiles: number;
  radiusGeoJson: GeoJSON.Feature | null;
  /** ZCTA boundary polygons returned by /geographic/zips-in-radius. */
  zipBoundaries?: GeoJSON.FeatureCollection | null;
}

function computeBbox(center: { lat: number; lng: number }, radiusMiles: number) {
  const rawPad = radiusMiles > 0 ? radiusMiles / 69 : 0;
  const lngPad = rawPad / Math.cos(center.lat * Math.PI / 180);
  return {
    minLat: center.lat - rawPad,
    maxLat: center.lat + rawPad,
    minLng: center.lng - lngPad,
    maxLng: center.lng + lngPad,
  };
}

const USMapWithRadius: React.FC<USMapWithRadiusProps> = ({
  center,
  radiusMiles,
  radiusGeoJson,
  zipBoundaries,
}) => {
  const mapRef = React.useRef<MapRef>(null);

  // Initial fit (used only for the first render — subsequent fits are
  // imperative via fitBounds below so the map animates instead of remounting).
  const initialBbox = React.useMemo(() => computeBbox(center, radiusMiles), [center, radiusMiles]);
  const initialFitLng = (initialBbox.minLng + initialBbox.maxLng) / 2;
  const initialFitLat = (initialBbox.minLat + initialBbox.maxLat) / 2;
  const initialSpan = Math.max(initialBbox.maxLat - initialBbox.minLat, initialBbox.maxLng - initialBbox.minLng);
  const initialFitZoom = initialSpan > 0 ? Math.max(3, Math.min(12, Math.log2(360 / (initialSpan * 1.5)))) : 10;

  // Animate the camera whenever the radius changes. The bbox is derived from
  // the radius alone (not the returned ZIPs) so the camera only animates once
  // per slider change — re-deriving from ZIPs would re-fire when the fetch
  // lands and produce a visible second zoom step.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const bbox = computeBbox(center, radiusMiles);
    map.fitBounds(
      [
        [bbox.minLng, bbox.minLat],
        [bbox.maxLng, bbox.maxLat],
      ],
      { padding: 40, duration: 350, maxZoom: 12 },
    );
  }, [center, radiusMiles]);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden relative" style={{ height: 480 }}>
      <Map
        ref={mapRef}
        key={`${center.lat.toFixed(2)}-${center.lng.toFixed(2)}`}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: initialFitLng, latitude: initialFitLat, zoom: initialFitZoom }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        projection={{ name: 'mercator' }}
        attributionControl={false}
        scrollZoom={false}
        boxZoom={false}
        doubleClickZoom={false}
        touchZoomRotate={false}
        touchPitch={false}
        dragRotate={false}
        dragPan={false}
        keyboard={false}
        onLoad={(e) => {
          // Mask non-US territory and hide country labels for a cleaner US-only look.
          const map = e.target;
          if (!map.getSource('country-boundaries')) {
            map.addSource('country-boundaries', { type: 'vector', url: 'mapbox://mapbox.country-boundaries-v1' });
          }
          if (!map.getLayer('non-us-mask')) {
            map.addLayer({
              id: 'non-us-mask',
              type: 'fill',
              source: 'country-boundaries',
              'source-layer': 'country_boundaries',
              filter: ['!=', ['get', 'iso_3166_1'], 'US'],
              paint: { 'fill-color': '#ffffff', 'fill-opacity': 1 },
            });
          }
          for (const layer of map.getStyle().layers || []) {
            if (layer.type === 'symbol' && (layer.id.includes('country') || layer.id.includes('continent'))) {
              map.setLayoutProperty(layer.id, 'visibility', 'none');
            }
          }
          if (map.getLayer('non-us-mask')) map.moveLayer('non-us-mask');
        }}
      >
        {zipBoundaries && zipBoundaries.features.length > 0 && (
          <Source id="zips-inside" type="geojson" data={zipBoundaries}>
            <MapLayer id="zips-inside-fill" type="fill" paint={{ 'fill-color': '#dbeafe', 'fill-opacity': 0.5 }} />
            <MapLayer id="zips-inside-border" type="line" paint={{ 'line-color': '#3b82f6', 'line-width': 1 }} />
            <MapLayer
              id="zips-inside-labels"
              type="symbol"
              layout={{ 'text-field': ['get', 'zcta'], 'text-size': 10, 'text-allow-overlap': false }}
              paint={{ 'text-color': '#1e40af', 'text-halo-color': '#ffffff', 'text-halo-width': 1 }}
            />
          </Source>
        )}
        {radiusMiles > 0 && (
          <Marker longitude={center.lng} latitude={center.lat}>
            <div className="w-3 h-3 bg-blue-600 rounded-full border-2 border-white shadow" />
          </Marker>
        )}
        {radiusGeoJson && (
          <Source id="radius-circle" type="geojson" data={radiusGeoJson}>
            <MapLayer id="radius-fill" type="fill" paint={{ 'fill-color': '#2563eb', 'fill-opacity': 0.08 }} />
            <MapLayer id="radius-outline" type="line" paint={{ 'line-color': '#2563eb', 'line-width': 2, 'line-dasharray': [3, 2] }} />
          </Source>
        )}
      </Map>
    </div>
  );
};

export default USMapWithRadius;
